<template>
  <div>
    <base-header class="pb-6" />
    <b-container fluid class="mt--6">
      <b-card no-body>
        <card-header title="Pagers">
          <b-button variant="primary" size="sm" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Pair
          </b-button>
        </card-header>

        <el-table
          :data="pairs"
          header-row-class-name="thead-light"
          v-loading="loading"
        >
          <el-table-column label="Device A" min-width="180">
            <template v-slot="{ row }">
              <div class="font-weight-bold">{{ memberName(row, 0) }}</div>
              <small class="text-muted">{{ memberId(row, 0) }}</small>
            </template>
          </el-table-column>

          <el-table-column label="" width="60" align="center">
            <template v-slot>
              <i class="fas fa-exchange-alt text-muted"></i>
            </template>
          </el-table-column>

          <el-table-column label="Device B" min-width="180">
            <template v-slot="{ row }">
              <div class="font-weight-bold">{{ memberName(row, 1) }}</div>
              <small class="text-muted">{{ memberId(row, 1) }}</small>
            </template>
          </el-table-column>

          <el-table-column label="Status" width="100">
            <template v-slot="{ row }">
              <b-badge :variant="row.active ? 'success' : 'secondary'">
                {{ row.active ? 'Active' : 'Inactive' }}
              </b-badge>
            </template>
          </el-table-column>

          <el-table-column label="Created" width="180">
            <template v-slot="{ row }">
              {{ formatDate(row.created_at) }}
            </template>
          </el-table-column>

          <el-table-column label="Actions" width="100">
            <template v-slot="{ row }">
              <b-button size="sm" variant="danger" @click="deletePair(row)">
                <i class="fas fa-trash"></i>
              </b-button>
            </template>
          </el-table-column>
        </el-table>
      </b-card>
    </b-container>

    <!-- Add Pair Modal -->
    <b-modal
      v-model="modalVisible"
      :title="provisioning ? 'Provisioning Pair' : 'Create Transcribe Pair'"
      size="lg"
      :no-close-on-backdrop="provisioning"
      :no-close-on-esc="provisioning"
      :hide-header-close="provisioning"
      @hidden="onModalHidden"
    >
      <!-- ───────── SELECTION VIEW ───────── -->
      <b-form v-if="!provisioning">
        <b-row>
          <b-col md="7">
            <b-form-group label="Available devices" label-for="device-search">
              <b-form-input
                id="device-search"
                v-model="searchQuery"
                placeholder="Search by id, site, or group..."
                size="sm"
                class="mb-2"
              ></b-form-input>

              <div class="device-list">
                <div
                  v-if="filteredDevices.length === 0"
                  class="text-muted text-center py-3"
                >
                  No devices match
                </div>
                <div
                  v-for="d in filteredDevices"
                  :key="d.device_id"
                  class="device-item"
                  :class="{ selected: !!slotFor(d.device_id) }"
                  @click="toggleDevice(d)"
                >
                  <div>
                    <strong>{{ d.device_id }}</strong>
                    <small class="text-muted ml-2">{{ d.site || 'no site' }}</small>
                  </div>
                  <b-badge v-if="slotFor(d.device_id)" variant="primary">
                    Slot {{ slotFor(d.device_id) }}
                  </b-badge>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <b-col md="5">
            <b-form-group label="Device A">
              <div class="selected-block">
                <strong v-if="form.device_a">{{ form.device_a }}</strong>
                <small v-else class="text-muted">Click a device on the left</small>
              </div>
              <b-form-input
                v-model="form.name_a"
                placeholder="Name (e.g. Hans)"
                size="sm"
                class="mt-2"
                :disabled="!form.device_a"
              ></b-form-input>
            </b-form-group>

            <b-form-group label="Device B">
              <div class="selected-block">
                <strong v-if="form.device_b">{{ form.device_b }}</strong>
                <small v-else class="text-muted">Click a device on the left</small>
              </div>
              <b-form-input
                v-model="form.name_b"
                placeholder="Name (e.g. Anna)"
                size="sm"
                class="mt-2"
                :disabled="!form.device_b"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-alert v-if="formError" variant="danger" show class="mt-3">
          {{ formError }}
        </b-alert>
      </b-form>

      <!-- ───────── PROVISIONING VIEW ───────── -->
      <div v-else>
        <b-card class="mb-3 border-primary">
          <b-row align-v="center">
            <b-col md="8">
              <h5 class="mb-1">
                <i
                  class="fas fa-sync-alt fa-spin text-primary"
                  v-if="!provisioningDone"
                ></i>
                <i
                  class="fas fa-check-circle text-success"
                  v-else-if="provisioningSuccess"
                ></i>
                <i
                  class="fas fa-exclamation-triangle text-danger"
                  v-else
                ></i>
                {{ provisioningMessage }}
              </h5>
              <p class="mb-0 text-muted">
                Pair: {{ form.device_a }} ↔ {{ form.device_b }}
              </p>
            </b-col>
            <b-col md="4" class="text-right">
              <h3 class="mb-0">{{ provisioningProgress }}%</h3>
            </b-col>
          </b-row>
          <b-progress
            :value="provisioningProgress"
            :max="100"
            height="20px"
            class="mt-3"
            :variant="provisioningVariant"
            :animated="!provisioningDone"
            :striped="!provisioningDone"
          ></b-progress>
        </b-card>

        <b-row>
          <b-col md="6" v-for="dev in provisioningDevices" :key="dev.device_id">
            <b-card :class="deviceCardClass(dev)">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong>{{ dev.device_id }}</strong>
                <b-badge :variant="deviceStatusVariant(dev)" pill>
                  {{ dev.status }}
                </b-badge>
              </div>
              <small class="text-muted d-block mb-2">{{ dev.message }}</small>
              <b-progress
                :value="deviceProgress(dev)"
                :max="100"
                height="14px"
                :variant="deviceProgressVariant(dev)"
              ></b-progress>
              <div class="mt-2 small text-muted">
                Files: {{ dev.files_sent }}/{{ dev.files_total || 4 }}
                <span v-if="dev.reboot_sent"> · Reboot sent</span>
                <span v-if="dev.allocated"> · ✅ Allocated</span>
              </div>
              <b-alert v-if="dev.error" variant="danger" show class="mt-2 mb-0">
                <small>{{ dev.error }}</small>
              </b-alert>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <template #modal-footer>
        <template v-if="!provisioning">
          <b-button variant="secondary" @click="modalVisible = false">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            @click="savePair"
            :disabled="!isFormValid"
          >
            Create
          </b-button>
        </template>
        <template v-else>
          <b-button
            variant="secondary"
            @click="closeProvisioningModal"
            :disabled="!provisioningDone"
          >
            {{ provisioningDone ? 'Close' : 'Provisioning...' }}
          </b-button>
        </template>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import ApiService from '@/services/api.service';
import { Action } from '@/store/actions';
import Swal from 'sweetalert2';

export default {
  name: 'TranscribePairsList',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  data() {
    return {
      pairs: [],
      devices: [],
      loading: false,
      modalVisible: false,
      formError: '',
      searchQuery: '',
      form: {
        device_a: null,
        device_b: null,
        name_a: '',
        name_b: '',
      },
      // Provisioning state
      provisioning: false,
      provisioningPairId: null,
      provisioningMessage: 'Starting...',
      provisioningProgress: 0,
      provisioningDevices: [],
      provisioningDone: false,
      provisioningSuccess: false,
      pollInterval: null,
      pollCount: 0,
    };
  },
  computed: {
    filteredDevices() {
      const busy = new Set();
      this.pairs.forEach((p) => {
        (p.members || []).forEach((m) => busy.add(m.device_id));
      });
      const q = (this.searchQuery || '').trim().toLowerCase();
      return this.devices
        .filter((d) => !busy.has(d.device_id))
        .filter(
          (d) =>
            !q ||
            d.device_id.toLowerCase().includes(q) ||
            (d.site && d.site.toLowerCase().includes(q)) ||
            (d.group && d.group.toLowerCase().includes(q))
        );
    },
    isFormValid() {
      return (
        this.form.device_a &&
        this.form.device_b &&
        this.form.device_a !== this.form.device_b &&
        this.form.name_a.trim() &&
        this.form.name_b.trim()
      );
    },
    provisioningVariant() {
      if (!this.provisioningDone) return 'primary';
      return this.provisioningSuccess ? 'success' : 'danger';
    },
  },
  methods: {
    memberName(row, idx) {
      const m = (row.members || [])[idx];
      return m ? m.name : '—';
    },
    memberId(row, idx) {
      const m = (row.members || [])[idx];
      return m ? m.device_id : '';
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString('de-DE', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    },

    slotFor(deviceId) {
      if (this.form.device_a === deviceId) return 'A';
      if (this.form.device_b === deviceId) return 'B';
      return null;
    },

    toggleDevice(d) {
      const id = d.device_id;
      const slot = this.slotFor(id);
      if (slot === 'A') { this.form.device_a = null; return; }
      if (slot === 'B') { this.form.device_b = null; return; }
      if (!this.form.device_a) this.form.device_a = id;
      else if (!this.form.device_b) this.form.device_b = id;
    },

    deviceCardClass(dev) {
      return {
        'border-success': dev.status === 'completed',
        'border-danger': dev.status === 'failed',
        'border-primary': dev.status !== 'completed' && dev.status !== 'failed',
      };
    },
    deviceStatusVariant(dev) {
      const map = {
        pending: 'secondary',
        sending_files: 'primary',
        files_sent: 'info',
        rebooting: 'warning',
        completed: 'success',
        failed: 'danger',
      };
      return map[dev.status] || 'secondary';
    },
    deviceProgress(dev) {
      if (dev.status === 'completed') return 100;
      if (dev.status === 'rebooting') return 80;
      if (dev.status === 'files_sent') return 60;
      if (dev.status === 'sending_files') {
        const total = dev.files_total || 4;
        return Math.round((dev.files_sent / total) * 50);
      }
      return 0;
    },
    deviceProgressVariant(dev) {
      if (dev.status === 'completed') return 'success';
      if (dev.status === 'failed') return 'danger';
      if (dev.status === 'rebooting') return 'warning';
      return 'primary';
    },

    async loadPairs() {
      this.loading = true;
      try {
        this.pairs = await this.$store.dispatch(Action.transcribePair.load);
      } catch (error) {
        // handled by notifyApiError
      } finally {
        this.loading = false;
      }
    },

    async loadDevices() {
      try {
        const response = await ApiService.getT('device-assignments/available-devices');
        this.devices = response.data.data.devices || [];
      } catch (error) {
        console.error('Failed to load devices:', error);
      }
    },

    openAddModal() {
      this.resetForm();
      this.modalVisible = true;
      if (this.devices.length === 0) this.loadDevices();
    },

    resetForm() {
      this.form = { device_a: null, device_b: null, name_a: '', name_b: '' };
      this.searchQuery = '';
      this.formError = '';
      this.provisioning = false;
      this.provisioningPairId = null;
      this.provisioningMessage = 'Starting...';
      this.provisioningProgress = 0;
      this.provisioningDevices = [];
      this.provisioningDone = false;
      this.provisioningSuccess = false;
      this.pollCount = 0;
    },

    onModalHidden() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      this.resetForm();
    },

    async savePair() {
      this.formError = '';

      // Prepare the provisioning view BEFORE the request, but don't show it yet.
      const aId = this.form.device_a;
      const bId = this.form.device_b;

      try {
        const result = await this.$store.dispatch(Action.transcribePair.add, {
          device_a: aId,
          device_b: bId,
          name_a: this.form.name_a.trim(),
          name_b: this.form.name_b.trim(),
        });

        // Response contains id and initial provisioning status
        this.provisioningPairId = result?.id || null;

        // Switch to provisioning view
        this.provisioning = true;
        this.provisioningDone = false;
        this.provisioningSuccess = false;
        this.provisioningMessage = 'Sending files...';
        this.provisioningProgress = 5;
        this.provisioningDevices = [
          { device_id: aId, status: 'pending', message: 'Waiting...', files_sent: 0, files_total: 4, reboot_sent: false, allocated: false, error: null },
          { device_id: bId, status: 'pending', message: 'Waiting...', files_sent: 0, files_total: 4, reboot_sent: false, allocated: false, error: null },
        ];

        // Begin polling
        this.startPolling();
      } catch (error) {
        const msg =
          error?.errorInfo?.['developer message'] ||
          error?.errorInfo?.userMessage ||
          error?.message ||
          'Failed to create pair';
        this.formError = msg;
      }
    },

    startPolling() {
      this.pollCount = 0;
      if (this.pollInterval) clearInterval(this.pollInterval);

      // Poll immediately, then every 2s
      this.pollStatus();
      this.pollInterval = setInterval(() => {
        this.pollCount += 1;
        // Give up after 3 minutes (90 polls)
        if (this.pollCount > 90) {
          this.stopPolling();
          this.provisioningDone = true;
          this.provisioningSuccess = false;
          this.provisioningMessage = 'Polling timeout';
          return;
        }
        this.pollStatus();
      }, 2000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    async pollStatus() {
      if (!this.provisioningPairId) return;
      try {
        const response = await ApiService.getT(
          `transcribe-pairs/status/${this.provisioningPairId}`
        );
        const data = response.data.data;

        this.provisioningMessage = data.message || 'Working...';
        this.provisioningProgress = data.progress || 0;
        this.provisioningDevices = Object.values(data.devices || {});

        const overall = data.overall_status;
        if (overall === 'completed' || overall === 'failed') {
          this.provisioningDone = true;
          this.provisioningSuccess = overall === 'completed';
          this.stopPolling();

          // Refresh the pair list so the new pair appears / failed one disappears
          await this.loadPairs();
        }
      } catch (error) {
        console.warn('Status poll failed:', error);
      }
    },

    closeProvisioningModal() {
      this.stopPolling();
      this.modalVisible = false;
      this.loadPairs();
    },

    async deletePair(row) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Deactivate pair ${this.memberName(row, 0)} ↔ ${this.memberName(row, 1)}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, deactivate it!',
      });
      if (!result.isConfirmed) return;

      try {
        await this.$store.dispatch(Action.transcribePair.delete, row.id);
        await this.loadPairs();
      } catch (error) {
        // handled by notifyApiError
      }
    },
  },
  mounted() {
    this.loadPairs();
    this.loadDevices();
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.device-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.device-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.device-item:last-child { border-bottom: none; }
.device-item:hover { background: #f8f9fa; }
.device-item.selected { background: #e7f1ff; }
.selected-block {
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 4px;
  min-height: 34px;
  display: flex;
  align-items: center;
}
</style>